// Club admin home (X102): the pure words and maths behind the setup guide, the Needs you list,
// the competition rows, the smart Invite players control and the New button. Nothing here
// talks to the API (the page does); the API's ClubDashboardService decides every done state
// and join state, this only turns them into words and targets.
import {formatExplainers} from '~/utils/formatExplainers';
import {entrantWord as entrantWordFor} from '~/utils/competitionsPage';
import {settingsRoute} from '~/utils/clubSettings';
import {COMPETITIONS_ROUTE, competitionsRoute} from '~/utils/competitionsPage';

// X103 G5: Run competitions is no longer a goal (Get started leads for every club); the API offers these four.
export const GUIDE_GOAL_ORDER = Object.freeze(['bookings', 'memberships', 'payments', 'onShow']);

/**
 * X144: the five formats are one admin Competitions page now (route ClubCompetitions, ?format= picks one); the old
 * per-format list routes are gone. Kept as a map so the callers that iterate the formats keep their shape.
 */
export const FORMAT_ROUTES = Object.freeze({
  league: COMPETITIONS_ROUTE,
  americano: COMPETITIONS_ROUTE,
  ladder: COMPETITIONS_ROUTE,
  tournament: COMPETITIONS_ROUTE,
  meet: COMPETITIONS_ROUTE,
});

/** X144: where "New <format>" goes: the Competitions page with that format's create wizard open. */
export function createFormatRoute(type, clubId) {
  return competitionsRoute(clubId, {create: type});
}

/** X144: that format's rows on the Competitions page. */
export function formatListRoute(type, clubId) {
  return competitionsRoute(clubId, {format: type});
}

export const FORMAT_COUNT_FIELDS = Object.freeze({
  league: 'leagueCount',
  americano: 'americanoCount',
  ladder: 'ladderCount',
  tournament: 'tournamentCount',
  meet: 'meetCount',
});

/** "box league", "americano league", "ladder", "tournament", "meet": the join line and the New button use it. */
export function formatWord(type) {
  return formatExplainers[type]?.singular ?? 'competition';
}

/** "New box league" / "New americano league" / "New ladder" / "New tournament" / "New meet". */
export function newLabel(type) {
  return `New ${formatWord(type)}`;
}

/** One line per format for the compact list (the explainer's tagline, fewer words than the old cards). */
export function formatRows() {
  // X167: the same alphabetical order as the New competition menus
  return formatKeysAlphabetical().map((key) => {
    const f = formatExplainers[key];
    return {key, name: f.name, icon: f.icon, tint: f.tint, ink: f.ink, line: f.tagline, route: FORMAT_ROUTES[key]};
  });
}

/**
 * The New control by the Your competitions heading: the club's own format(s). One format → one button that names
 * it, with "Other format…" beside it; two or more → a menu. X167 (Grant 2026-10-27: "why are americano and meet
 * greyed out, the list is not alphabetical and there are no icons for each format"): the menu itself is
 * newCompetitionMenu(), ONE alphabetical list of all five formats with their icons, never the club's formats first
 * with the others greyed under a divider.
 */
export function newCompetitionControl(summaryRows) {
  const used = [];
  for (const r of summaryRows ?? []) {
    if (r?.type && FORMAT_ROUTES[r.type] && !used.includes(r.type)) used.push(r.type);
  }
  const items = used.map((type) => ({type, label: newLabel(type), route: FORMAT_ROUTES[type]}));
  if (items.length === 0) return {kind: 'none', items: []};
  if (items.length === 1) return {kind: 'single', items};
  return {kind: 'menu', items};
}

/** X167: the five formats in alphabetical order of their words (americano league, box league, ladder, meet, tournament). */
export function formatKeysAlphabetical() {
  return Object.keys(FORMAT_ROUTES).sort((a, b) => formatWord(a).localeCompare(formatWord(b), 'en'));
}

/**
 * X167: every New competition menu (the Competitions page's header, the club home's Your competitions New button) is
 * this ONE list: all five formats, alphabetical, in normal text, each with its identity icon (utils/formatExplainers,
 * the single source: trophy = tournaments only, handshake = meets). The assistant row comes BEFORE it, on the page
 * (Grant 2026-11-02: the setup assistant is the first option on both platforms, then the formats).
 */
export function newCompetitionMenu() {
  // X191 (Grant 2026-10-27: "the icons … are too big, not matching the UI, and they should be the same colour as their
  // corresponding competition type"): each row carries its format's identity tint + ink for the small .tl-menu-format-ic tile
  return formatKeysAlphabetical().map((type) => ({
    type, label: newLabel(type), icon: competitionIcon(type), route: FORMAT_ROUTES[type],
    tint: formatExplainers[type]?.tint, ink: formatExplainers[type]?.ink,
  }));
}

/** Where a competition row opens (its admin page). */
export function competitionRoute(row, clubId) {
  const id = row?.id;
  switch (row?.type) {
    case 'league': return {name: 'ClubLeagueManage', params: {clubId, leagueId: id}};
    case 'americano': return {name: 'ClubAmericanoManage', params: {clubId, americanoId: id}};
    case 'ladder': return {name: 'ClubLadderManage', params: {clubId, ladderId: id}};
    case 'tournament': return {name: 'ClubTournamentManage', params: {clubId, tournamentId: id}};
    case 'meet': return {name: 'ClubMeetManage', params: {clubId, meetId: id}};
    default: return {name: 'ClubDashboard', params: {clubId}};
  }
}

export function competitionIcon(type) {
  return formatExplainers[type]?.icon ?? 'mdi-medal-outline';
}

/**
 * X107 (R43 M2, R43 L): where a competition's overdue MATCHES are, for the Needs you row about them.
 * X220 (Grant 2026-10-28: "Navigating from the admin dashboard via the Needs you links doesn't change the template"):
 * the admin stays in the admin. A box or americano league opens its admin page on that cycle (Cycles & divisions,
 * "N results to go"), a tournament its admin Draw tab, a meet its admin page (its dates). A ladder's challenges are
 * only on its public page (the admin page has Players, Settings, Payments), so that one is `public`: it opens in a
 * new tab, the way View public page does, never a same-tab jump out of the admin.
 *   {kind: 'route', to}   – an admin page (same tab)
 *   {kind: 'public', to}  – the competition's public page (a new tab)
 */
export function matchesTarget(type, id, clubId, cycleId = null) {
  switch (type) {
    case 'ladder': return {kind: 'public', to: {name: 'LadderDetail', params: {ladderId: id}, query: {tab: 'challenges'}}};
    case 'league':
    case 'americano': {
      const to = competitionRoute({type, id}, clubId);
      return {kind: 'route', to: cycleId ? {...to, query: {cycle: cycleId}} : to};
    }
    case 'tournament': return {kind: 'route', to: {...competitionRoute({type, id}, clubId), query: {tab: 'draw'}}};
    case 'meet': return {kind: 'route', to: competitionRoute({type, id}, clubId)};
    default: return null;
  }
}

/** X220: whether a Needs you / chooser target opens the public page in a new tab (`public`) rather than an admin page. */
export const opensPublicPage = (target) => target?.kind === 'public';

/** X107 (R41 L2): the cycle-ending row opens the league's page WITH the rollover wizard (?rollover=1 on that cycle). */
export function rolloverRoute(type, id, clubId, cycleId = null) {
  const to = competitionRoute({type, id}, clubId);
  return {...to, query: {...(cycleId ? {cycle: cycleId} : {}), rollover: '1'}};
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Defaults for the words below: a short day-month and a 24-hour time. The page passes the
// localised formatters from useDates; these keep the util testable on its own.
const defaultDayMonth = (iso) => new Date(iso).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});
const defaultWeekday = (iso) => new Date(iso).toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'});
const defaultTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit', hour12: false});

/**
 * The one line under a competition's name: progress and what is next, e.g.
 * "Cycle 1 · 12 results to go · ends 12 Nov". Entrants are teams for leagues and tournaments,
 * players for ladders, americanos and meets (the API's Entrants means that already).
 */
export function competitionLine(row, {dayMonth = defaultDayMonth, weekday = defaultWeekday, time = defaultTime} = {}) {
  if (!row) return '';
  const parts = [];
  // X165 (R76 L5): a singles league or cup enters players, not teams (utils/competitionsPage entrantWord)
  const entrantWord = entrantWordFor(row);
  switch (row.type) {
    case 'league':
    case 'americano': {
      if (row.running) {
        if (row.cycleName) parts.push(row.cycleName);
        const toGo = (row.resultsTotal ?? 0) - (row.resultsDone ?? 0);
        if ((row.resultsTotal ?? 0) === 0) parts.push('no matches yet');
        else if (toGo === 0) parts.push('all results in');
        else parts.push(plural(toGo, 'result to go', 'results to go'));
        if (row.endsAt) parts.push(`ends ${dayMonth(row.endsAt)}`);
      } else if (row.upcoming) {
        if (row.cycleName) parts.push(row.cycleName);
        if (row.startsAt) parts.push(`starts ${dayMonth(row.startsAt)}`);
        parts.push(plural(row.entrants ?? 0, ...entrantWord));
      } else if (row.cycleId && row.published === false) {
        parts.push(`${row.cycleName ?? 'Next cycle'} not published yet`);
      } else if (isSettingUp(row)) {
        // X150: the Competitions page's words for a just-created one. X165 (R76 M1): the X162 status rule also
        // makes a cycle with no divisions, or divisions with nobody seated, "Setting up": its line says which.
        parts.push(settingUpCycleWords(row));
      } else {
        parts.push('No cycle running');
      }
      break;
    }
    case 'ladder': {
      if (isSettingUp(row)) {
        parts.push(`No ${entrantWord[1]} yet`); // X165 (R76 M2): nobody in it = setting up; X166 (R77 L5): a doubles ladder says teams
      } else if (row.upcoming) {
        parts.push(`starts ${dayMonth(row.startsAt)}`);
        parts.push(plural(row.entrants ?? 0, ...entrantWord));
      } else if (!row.running) {
        parts.push(row.endsAt ? `ended ${dayMonth(row.endsAt)}` : 'Not running');
      } else {
        parts.push(plural(row.entrants ?? 0, ...entrantWord));
        parts.push(plural(row.openChallenges ?? 0, 'open challenge', 'open challenges'));
        if (row.endsAt) parts.push(`ends ${dayMonth(row.endsAt)}`);
      }
      break;
    }
    case 'tournament': {
      if (row.published === false) {
        parts.push('Draft, not published');
        parts.push(plural(row.entrants ?? 0, ...entrantWord));
      } else if (isSettingUp(row)) {
        parts.push(`No ${entrantWord[1]} yet`); // X165 (R76 M2): published with nobody entered = setting up
      } else if (!row.running && !row.upcoming) {
        parts.push(row.resultsTotal ? 'Finished' : (row.endsAt ? `ended ${dayMonth(row.endsAt)}` : 'Finished'));
      } else {
        parts.push(plural(row.entrants ?? 0, ...entrantWord));
        if (row.resultsTotal) parts.push(`${row.resultsDone ?? 0} of ${plural(row.resultsTotal, 'match played', 'matches played')}`);
        else if (row.upcoming && row.startsAt) parts.push(`starts ${dayMonth(row.startsAt)}`);
        else parts.push('no draw yet');
      }
      break;
    }
    case 'meet': {
      if (row.nextAt) {
        parts.push(`Next ${weekday(row.nextAt)} ${time(row.nextAt)}`);
        parts.push(plural(row.going ?? 0, 'going', 'going'));
      } else {
        parts.push(isSettingUp(row) ? 'No dates yet' : 'No dates to come'); // X150: the Competitions page's words
      }
      break;
    }
    default:
      break;
  }
  return parts.join(' · ');
}

/**
 * X150 (Grant 2026-10-26, R72 L6): a competition still being set up (the API's settingUp / status "settingUp": a
 * league or americano with no cycle yet, a meet with no dates yet, an unpublished tournament) is never "not running":
 * the home lists it as "Setting up", the same rule and chip as the Competitions page.
 */
export function isSettingUp(row) {
  return row?.settingUp === true || row?.status === 'settingUp';
}

/**
 * X165 (R76 M1): the line for a box league or americano that is setting up, following the X162 status rule: no cycle
 * at all = "No cycle yet"; a cycle with no divisions = "Cycle 1 · no divisions yet"; divisions with nobody seated =
 * "Cycle 1 · nobody seated yet" (the API's divisions count says which; without it the cycle's name alone is shown).
 */
export function settingUpCycleWords(row) {
  if (!row?.cycleId) return 'No cycle yet';
  const name = row.cycleName ?? 'Next cycle';
  if (row.divisions === 0) return `${name} · no divisions yet`;
  if (row.divisions > 0 || (row.entrants ?? 0) === 0) return `${name} · nobody seated yet`;
  return name;
}

/** A row the home always lists: running, starting soon or still being set up. */
export function isLiveCompetition(row) {
  return !!(row?.running || row?.upcoming || isSettingUp(row));
}

/**
 * The rows the competitions card shows: running, upcoming and setting-up ones always; the rest folded behind
 * "N not running" unless opened (a club with 25 competitions and 5 running must not read as 25).
 * With nothing running, the first few idle ones show so the card is never empty.
 */
export function visibleCompetitions(rows, showAll = false, idleWhenNoneRunning = 5) {
  const all = rows ?? [];
  const live = all.filter((r) => isLiveCompetition(r));
  const idle = all.filter((r) => !isLiveCompetition(r));
  if (showAll || idle.length === 0) return {shown: all, hidden: 0};
  if (live.length === 0) return {shown: idle.slice(0, idleWhenNoneRunning), hidden: Math.max(0, idle.length - idleWhenNoneRunning)};
  return {shown: live, hidden: idle.length};
}

/** 0–1 for the thin progress bar on a running row (null = no bar). */
export function competitionProgress(row) {
  if (!row?.running || !row.resultsTotal) return null;
  return Math.min(1, Math.max(0, (row.resultsDone ?? 0) / row.resultsTotal));
}

// ── Setup guide ──

/** "4 of 9 done" for the collapsed card and the head. */
export function guideProgressLabel(guide) {
  if (!guide) return '';
  return `${guide.done ?? 0} of ${guide.total ?? 0} done`;
}

/** X176: the phone dock's count, "4 of 9". */
export function guideCountLabel(guide) {
  if (!guide) return '';
  return `${guide.done ?? 0} of ${guide.total ?? 0}`;
}

/** X176: the next thing to do (the first item not done, in the guide's order), or null when everything is done. */
export function nextGuideItem(guide) {
  for (const g of guide?.groups ?? []) {
    const i = (g.items ?? []).find((x) => !x.done);
    if (i) return i;
  }
  return null;
}

/** The first group with something left opens by itself; null when everything is done. */
export function firstOpenGroupKey(guide) {
  return (guide?.groups ?? []).find((g) => (g.done ?? 0) < (g.total ?? 0))?.key ?? null;
}

/**
 * Where a guide item (or a Needs you row) opens. `dialog` targets act IN PLACE on the page
 * (the page owns the dialog); `route` targets are pages. The API hands over the key, never a path.
 * X103 G3: Club details, Who can see the club and the club's join settings live on the Club settings
 * page (its tabs); the logo keeps its dialog in place (the settings page's Logo tab opens the same one).
 */
export function openTarget(key, clubId) {
  switch (key) {
    case 'clubDetails': return {kind: 'route', to: settingsRoute(clubId, 'details')};
    case 'privacy': return {kind: 'route', to: settingsRoute(clubId, 'visibility')};
    case 'joinSettings': return {kind: 'route', to: settingsRoute(clubId, 'joining')};
    case 'logo': return {kind: 'dialog', name: 'logo'};
    case 'invite': return {kind: 'dialog', name: 'invite'};
    case 'newCompetition': return {kind: 'dialog', name: 'newCompetition'};
    case 'joinRequests': return {kind: 'dialog', name: 'joinRequests'};
    // X216: the club's country, answered in place on the dashboard.
    case 'country': return {kind: 'dialog', name: 'country'};
    case 'playingAreas': return {kind: 'route', to: {name: 'ClubPlayingAreas', params: {clubId}}};
    case 'players': return {kind: 'route', to: {name: 'ClubMembers', params: {clubId}}};
    case 'memberImport': return {kind: 'route', to: {name: 'ClubMemberImport', params: {clubId}}};
    // X186 (Grant 2026-10-27: "It should show a dialog, not just a random competition you think we want to enter a match
    // for"): First match played asks which competition (the home's EnterResultDialog), and a club-wide results row
    // (Needs you's "N results to approve") asks which competition's results; neither ever picks the first one.
    case 'competitions': return {kind: 'dialog', name: 'enterResult'};
    case 'results': return {kind: 'dialog', name: 'approveResults'};
    case 'followRequests': return {kind: 'route', to: {name: 'ClubFollowRequests', params: {clubId}}};
    case 'payments': return {kind: 'route', to: {name: 'ClubPayments', params: {clubId}}};
    case 'memberships': return {kind: 'route', to: {name: 'ClubMemberships', params: {clubId}}};
    case 'bookingRules': return {kind: 'route', to: {name: 'ClubBookings', params: {clubId}, query: {tab: 'rules'}}};
    // X133: "Open for bookings" starts the switch-on itself (?setup=1: the time-zone question or the save, in place on
    // whatever the Bookings page shows for the club; when nothing can be switched on yet the page shows the step and why).
    case 'bookingSettings': return {kind: 'route', to: {name: 'ClubBookings', params: {clubId}, query: {setup: '1'}}};
    case 'bookingCalendar': return {kind: 'route', to: {name: 'ClubBookings', params: {clubId}}};
    case 'widgets': return {kind: 'route', to: {name: 'ClubWidgets', params: {clubId}}};
    case 'displays': return {kind: 'route', to: {name: 'ClubDisplays', params: {clubId}}};
    default: return {kind: 'route', to: {name: 'ClubDashboard', params: {clubId}}};
  }
}

/**
 * A Needs you row's target: cycleEnding goes to that league's own page (the rollover lives there);
 * an overdue-results row (X103, one per running competition) opens the competition that holds the matches.
 * X220: every `route` target is an admin page; a `public` one opens the public page in a new tab (opensPublicPage).
 */
export function needsYouTarget(item, clubId) {
  // X107 (R41 L2): the cycle ending with nothing after it opens the rollover / new-cycle wizard on that cycle.
  if (item?.key === 'cycleEnding' && item.entityId) {
    return {kind: 'route', to: rolloverRoute(item.type, item.entityId, clubId, item.cycleId ?? null)};
  }
  // X107 (R43 M2): overdue results open where the matches are. X220: on the competition's admin page (its cycle, its
  // draw, its dates); a ladder's challenges, which only its public page has, in a new tab (matchesTarget).
  if (item?.key === 'overdueResults' && item.entityId) {
    return matchesTarget(item.type, item.entityId, clubId, item.cycleId ?? null)
      ?? {kind: 'route', to: competitionRoute({type: item.type, id: item.entityId}, clubId)};
  }
  // X177: players waiting to be placed open the competition's page on the tab where placing happens: a box or americano
  // league's cycle (its waiting-area card, scrolled into view by ?place=1), a ladder's Players tab (its default tab).
  if (item?.key === 'waitingToPlace' && item.entityId) {
    const to = competitionRoute({type: item.type, id: item.entityId}, clubId);
    if (item.type === 'league' || item.type === 'americano') {
      return {kind: 'route', to: {...to, query: {...(item.cycleId ? {cycle: item.cycleId} : {}), place: '1'}}};
    }
    return {kind: 'route', to};
  }
  return openTarget(item?.open, clubId);
}

export function needsYouIcon(key) {
  return {
    joinRequests: 'mdi-account-plus-outline',
    waitingToPlace: 'mdi-account-clock-outline',
    resultsToApprove: 'mdi-check-decagram-outline',
    overdueResults: 'mdi-clock-alert-outline',
    followRequests: 'mdi-account-heart-outline',
    failedPayments: 'mdi-credit-card-off-outline',
    renewalsFailing: 'mdi-card-account-details-outline',
    cycleEnding: 'mdi-autorenew',
    privacy: 'mdi-lock-outline',
    country: 'mdi-earth',
  }[key] ?? 'mdi-bell-outline';
}

// ── Invite players (the X102 addition): one smart control ──

/**
 * Which link to recommend, from the dashboard summary's rows (the API already decided each row's
 * open-to-joining state):
 *   none    – no competitions: create one, the club link is the fallback
 *   one     – one competition open to joining: share its link
 *   several – more than one: choose which, "Just the club" last
 *   closed  – competitions but none open: "Let people join <name>" (its join settings in place)
 */
export function inviteState(summaryRows) {
  const rows = (summaryRows ?? []).filter((r) => r && r.type);
  if (rows.length === 0) return {kind: 'none', open: [], closed: null};
  const open = rows.filter((r) => r.openToJoining && r.joinReferenceId);
  if (open.length === 1) return {kind: 'one', open, closed: null};
  if (open.length > 1) return {kind: 'several', open, closed: null};
  // Nothing open: the running one (or the first) is the one to switch on.
  const closed = rows.find((r) => (r.running || r.upcoming) && r.joinReferenceId) ?? rows[0];
  return {kind: 'closed', open: [], closed};
}

/** "Joins the club and the box league". */
export function joinsLine(row) {
  return `Joins the club and the ${formatWord(row?.type)}`;
}

/** Everything a week card needs to know to say "Nothing this week". */
export function weekIsEmpty(week) {
  if (!week) return true;
  return !(week.matches?.length || week.meetDates?.length || week.recentResults?.length || (week.bookingsToday ?? 0) > 0);
}

const isScore = (v) => v != null && v !== '' && /^\d+(\.\d+)?$/.test(String(v).trim());
const resultKind = (v) => {
  const w = String(v ?? '').trim().toLowerCase();
  if (w === 'w' || w === 'win' || w === 'won' || w === 'walkover' || w === 'w/o') return 'win';
  if (w === 'l' || w === 'loss' || w === 'lost' || w === 'forfeit' || w === 'forfeited' || w === 'conceded') return 'loss';
  return null;
};
const isWalkover = (home, away) => [home, away].some((v) => /walkover|w\/o|forfeit|concede/i.test(String(v ?? '')));

/**
 * "Aces v Bears" with the result when there is one. Scores read "Aces 3–1 Bears"; result words never run into
 * the names (X107 item 3, R41 M2 / R43 L7): "Ada Lovelace beat Ben Brown", "Ben Brown won by walkover v Ada Lovelace".
 */
export function matchWords(m) {
  if (!m) return '';
  const {home, away, homeResult, awayResult} = m;
  const has = homeResult != null && awayResult != null && (homeResult !== '' || awayResult !== '');
  if (!has) return `${home} v ${away}`;
  if (isScore(homeResult) && isScore(awayResult)) return `${home} ${homeResult}–${awayResult} ${away}`;
  const h = resultKind(homeResult);
  const a = resultKind(awayResult);
  const walkover = isWalkover(homeResult, awayResult);
  const winner = h === 'win' || a === 'loss' ? home : a === 'win' || h === 'loss' ? away : null;
  const loser = winner === home ? away : winner === away ? home : null;
  if (winner && walkover) return `${winner} won by walkover v ${loser}`;
  if (winner) return `${winner} beat ${loser}`;
  return `${home} v ${away} · result in`;
}
